import { useEffect, useRef, useState, useCallback } from 'react';
import { io, type Socket } from 'socket.io-client';
import { message } from 'antd';
import { listarPendencias, type Pendencia } from '../api/pendencias';
import { useAuthStore, getAuthToken } from '../store/authStore';
import { playBeep, showSystemNotification, isWindowVisible, ensureNotificationPermission } from '../lib/notify';
import { setupPushSubscription } from '../lib/push';

/**
 * Hook que gerencia a lista de pendências de um cliente com tempo real.
 * - Carga inicial via REST
 * - Conecta no Socket.IO, entra na sala cliente:<id>
 * - Escuta pendencia:nova / pendencia:atualizada e atualiza o estado
 * - Toca som + Browser Notification quando a aba não está focada (e a pendência é nova)
 */
export function useChatPendencias(clienteId: number | null) {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const [lista, setLista] = useState<Pendencia[]>([]);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  // Pede permissão de Notification uma vez (idempotente — se já decidido, não pergunta de novo).
  // Em seguida, registra service worker + push subscription pra notificações com a aba fechada.
  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      const perm = await ensureNotificationPermission();
      if (perm === 'granted') {
        await setupPushSubscription();
      }
    })();
  }, [isAuthenticated]);

  // Carga REST inicial
  useEffect(() => {
    if (!clienteId || !isAuthenticated) {
      setLista([]);
      return;
    }
    setLoading(true);
    listarPendencias(clienteId)
      .then(setLista)
      .catch(() => { /* já tratado no client */ })
      .finally(() => setLoading(false));
  }, [clienteId, isAuthenticated]);

  // Conecta socket
  useEffect(() => {
    if (!isAuthenticated || !clienteId) return;
    // Token vem ou de sessionStorage (login explícito) ou null (cookie .grupomultiluz).
    // Nos dois casos o servidor sabe autenticar.
    const token = getAuthToken();

    const socket = io({
      path: '/api/socket.io',
      // Token explícito quando há sessionStorage; servidor também aceita cookie
      // mtz_auth via withCredentials (caso típico dentro de iframe).
      auth: token ? { token } : undefined,
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('join:cliente', clienteId);
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('connect_error', (err) => {
      console.warn('[realtime] connect_error', err.message);
      setConnected(false);
    });

    const handleNova = (p: Pendencia) => {
      setLista((prev) => {
        if (prev.some((x) => x.id === p.id)) return prev;
        return [p, ...prev];
      });
      // Não notifica o próprio autor (já viu na hora de criar)
      if (p.criadaPor.email === user?.email) return;
      message.info(`Nova pendência de ${p.criadaPor.nome}`);
      if (!isWindowVisible()) {
        playBeep(220, 880);
        showSystemNotification(
          `Pendência — ${p.criadaPor.nome}`,
          p.descricao.length > 120 ? p.descricao.slice(0, 117) + '…' : p.descricao,
          () => { window.location.href = `/homologacoes/${p.clienteId}`; }
        );
      } else {
        playBeep(120, 880);
      }
    };

    const handleAtualizada = (p: Pendencia) => {
      setLista((prev) => prev.map((x) => (x.id === p.id ? p : x)));
      // Vendedor marcou como resolvida → engenharia precisa concluir. Avisa quem
      // está vendo o cliente (exceto quem resolveu).
      if (p.status === 'resolvida' && p.resolvidaPor?.email !== user?.email) {
        message.info(`Pendência resolvida por ${p.resolvidaPor?.nome} — pronta para concluir`);
        if (!isWindowVisible()) {
          playBeep(180, 760);
          showSystemNotification(
            `Pendência resolvida`,
            `${p.resolvidaPor?.nome} resolveu: ${p.descricao.slice(0, 80)}`,
            () => { window.location.href = `/homologacoes/${p.clienteId}`; },
          );
        }
      }
      // Avisa quem criou que foi concluída
      if (p.status === 'concluida' && p.criadaPor.email === user?.email && p.concluidaPor?.email !== user?.email) {
        message.success(`Pendência concluída por ${p.concluidaPor?.nome}`);
        if (!isWindowVisible()) {
          playBeep(150, 660);
          showSystemNotification(
            `Pendência concluída`,
            `${p.concluidaPor?.nome} fechou: ${p.descricao.slice(0, 80)}`,
          );
        }
      }
    };

    socket.on('pendencia:nova', handleNova);
    socket.on('pendencia:atualizada', handleAtualizada);

    return () => {
      socket.off('pendencia:nova', handleNova);
      socket.off('pendencia:atualizada', handleAtualizada);
      socket.emit('leave:cliente', clienteId);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [clienteId, isAuthenticated, user?.email]);

  const refresh = useCallback(async () => {
    if (!clienteId) return;
    const d = await listarPendencias(clienteId);
    setLista(d);
  }, [clienteId]);

  return { lista, loading, connected, refresh, setLista };
}
